/**
 * The stream-configuration options an operator can set on a preview.
 *
 * One definition, two surfaces: the share dialog bakes these into a share
 * record, and the preview panel lets you change them on a live session. They
 * used to exist only in the share dialog's JSX, which is why the preview could
 * not offer them — anything added here appears in both.
 */

/** Named resolutions, mirroring `RESOLUTION_CLAMP_MAP` in @interlucent/pixel-stream. */
export const RESOLUTION_PRESETS = [
  { value: 'sxga', label: 'SXGA — 1280×1024' },
  { value: 'hd', label: 'HD — 1366×768' },
  { value: 'hdplus', label: 'HD+ — 1600×900' },
  { value: 'fhd', label: 'FHD — 1920×1080' },
  { value: 'wuxga', label: 'WUXGA — 1920×1200' },
  { value: 'qhd', label: 'QHD — 2560×1440' },
  { value: 'wqhd', label: 'WQHD — 3440×1440' },
  { value: 'uhd', label: 'UHD — 3840×2160' },
] as const;

/** Sentinel for the select; never sent to the SDK. */
export const CUSTOM_RESOLUTION = '__custom__';

export const STREAM_QUALITY_OPTIONS = [
  { value: 'low-latency', label: 'Low latency' },
  { value: 'balanced', label: 'Balanced' },
  { value: 'quality', label: 'Quality' },
  { value: 'cinematic', label: 'Cinematic (default)' },
  { value: 'unbounded', label: 'Unbounded — max bitrate, zero jitter buffer' },
] as const;

export interface StreamConfig {
  pointerLock?: boolean;
  nativeTouch?: boolean;
  suppressBrowserKeys?: boolean;
  useMic?: boolean;
  forceRelay?: boolean;
  /** Named preset or "WIDTHxHEIGHT". */
  resolutionClamp?: string;
  /** Named preset or "WIDTHxHEIGHT". Wins over the clamp where they conflict. */
  resolutionFloor?: string;
  streamQuality?: string;
}

/**
 * Options that cannot take effect on a running session.
 *
 * `useMic` and `forceRelay` are decided before the offer exists (microphone
 * acquisition precedes the audio transceiver; ICE transport policy is fixed at
 * peer-connection construction), and `streamQuality` is baked into the munged
 * SDP. Changing any of them needs a new session.
 *
 * Everything else is live: the SDK re-reads input options per event, and
 * `resolutionClamp` / `resolutionFloor` apply on the next resolution send,
 * which `refreshResolution()` triggers on demand.
 */
export const SESSION_SCOPED_KEYS: ReadonlyArray<keyof StreamConfig> = [
  'useMic',
  'forceRelay',
  'streamQuality',
];

/** True when moving `from` → `to` requires tearing the session down. */
export function needsReconnect(from: StreamConfig, to: StreamConfig): boolean {
  return SESSION_SCOPED_KEYS.some((k) => (from[k] ?? undefined) !== (to[k] ?? undefined));
}

/** True when the change only affects what resolution we ask for. */
export function needsResolutionRefresh(from: StreamConfig, to: StreamConfig): boolean {
  return from.resolutionClamp !== to.resolutionClamp || from.resolutionFloor !== to.resolutionFloor;
}

/**
 * Split a stored value into the select's value and the custom W/H boxes.
 * A `WIDTHxHEIGHT` string that is not a named preset drives the custom mode.
 */
export function splitResolutionValue(value: string | undefined): {
  preset: string;
  width: string;
  height: string;
} {
  if (!value) return { preset: '', width: '', height: '' };
  if (RESOLUTION_PRESETS.some((p) => p.value === value)) {
    return { preset: value, width: '', height: '' };
  }
  const dims = value.match(/^(\d+)x(\d+)$/);
  if (dims) return { preset: CUSTOM_RESOLUTION, width: dims[1], height: dims[2] };
  return { preset: '', width: '', height: '' };
}

/**
 * Build the stored value from the select + custom boxes, or undefined when the
 * selection is incomplete. Returning undefined rather than a partial string is
 * what keeps a half-typed "1600x" out of a share record.
 */
export function joinResolutionValue(
  preset: string,
  width: string,
  height: string,
): string | undefined {
  if (!preset) return undefined;
  if (preset !== CUSTOM_RESOLUTION) return preset;
  const w = parseInt(width, 10);
  const h = parseInt(height, 10);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return undefined;
  return `${w}x${h}`;
}

/** Drop empty/false entries so a share record carries only what was set. */
export function compactStreamConfig(cfg: StreamConfig): Record<string, unknown> | undefined {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(cfg)) {
    if (v === undefined || v === '' || v === false) continue;
    out[k] = v;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** Map a stored share record back onto the form's shape. */
export function streamConfigFromRecord(record: Record<string, unknown> | null | undefined): StreamConfig {
  if (!record) return {};
  const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined);
  return {
    pointerLock: !!record.pointerLock,
    nativeTouch: !!record.nativeTouch,
    suppressBrowserKeys: !!record.suppressBrowserKeys,
    useMic: !!record.useMic,
    forceRelay: !!record.forceRelay,
    resolutionClamp: str(record.resolutionClamp),
    resolutionFloor: str(record.resolutionFloor),
    streamQuality: str(record.streamQuality),
  };
}

/**
 * The `<pixel-stream>` attributes for a config.
 *
 * Booleans collapse to `true | undefined` because the element treats attribute
 * PRESENCE as on — passing `false` would still set the attribute.
 */
export function streamConfigToAttributes(cfg: StreamConfig) {
  return {
    'use-mic': cfg.useMic ? true : undefined,
    'pointer-lock': cfg.pointerLock ? true : undefined,
    'native-touch': cfg.nativeTouch ? true : undefined,
    'suppress-browser-keys': cfg.suppressBrowserKeys ? true : undefined,
    'force-relay': cfg.forceRelay ? true : undefined,
    'resolution-clamp': cfg.resolutionClamp || undefined,
    'resolution-floor': cfg.resolutionFloor || undefined,
  } as const;
}
